import {
  Badge,
  Box,
  Group,
  Icon,
  Text,
  VisuallyHidden,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import {
  faArrowTurnDownRight,
  faCheck,
  faComment,
  faEye,
  faHeart,
  faThumbsUp,
} from '@inflearn/pro-regular-svg-icons';
import { unescape } from 'he';
import { useTranslation } from 'next-i18next';

import { FormattedTime, useLang } from '@inflearn/i18n-tools';
import type { PostByUserResponse, PostStatus } from '../../../../../resources/@types/uccSchema';
import { POST_STATUS, POST_TYPE } from '../../../../constants/post';
import { UCC_URL } from '../../../../constants/urls';
import { filterHTMLTag } from '../../../../utils/filterHTMLTag';
import { formatCompactNumber } from '../../../../utils/format/number';
import {
  type CommunityPostType,
  isAnswer,
  isQuestion,
  isResolvedStatus,
  isStudy,
} from '../../../../utils/post/postItemType';
import { replaceHtml } from '../../../../utils/replaceHtml';

type Props = PostByUserResponse;

export default function PostItem({
  body,
  commentCount,
  commentId,
  createdAtUTC,
  id,
  recommendedCount,
  status,
  title,
  type,
  viewCount,
  slug,
}: Props) {
  const { t } = useTranslation();
  const { lang } = useLang();
  const isAnswerType = isAnswer(type);

  return (
    <a
      href={
        isAnswerType && commentId
          ? UCC_URL.COMMUNITY_DETAIL_COMMENT({
              type: 'question',
              id: id,
              commentId: commentId,
              slug,
            })
          : UCC_URL.COMMUNITY_DETAIL({ type: type as CommunityPostType, id: id, slug })
      }
      aria-label={t('커뮤니티 상세페이지로 이동')}
    >
      <Box py={20}>
        <Group position="apart" css={{ [desktopFirstMediaQuery.mobile]: { p: { fontSize: 12 } } }}>
          <Group spacing={4}>
            <Text size="sm">{t(POST_TYPE[type])}</Text>
            {!isAnswerType && status ? <PostStatusText status={status} type={type} /> : null}
          </Group>
          <Text size="sm" color="gray.6">
            <FormattedTime
              dateTime={createdAtUTC}
              format={isAnswerType ? 'L' : 'L HH:mm'}
              locale={lang}
            />
          </Text>
        </Group>
        <Text
          weight={500}
          size="lg"
          mt={4}
          mb={8}
          lineClamp={2}
          css={{
            wordBreak: 'break-all',
            [desktopFirstMediaQuery.mobile]: { fontSize: 16, marginBottom: 8 },
          }}
        >
          {title}
        </Text>
        {isAnswerType && body ? (
          <Text
            size="md"
            mb={12}
            weight={500}
            lineClamp={2}
            color="gray.7"
            css={{
              wordBreak: 'break-all',
              [desktopFirstMediaQuery.mobile]: {
                fontSize: 14,
                marginBottom: 12,
              },
            }}
          >
            <Icon
              size="sm"
              color="gray.6"
              icon={faArrowTurnDownRight}
              css={{
                marginRight: 6,
              }}
            />

            {unescape(filterHTMLTag(replaceHtml(body)))}
          </Text>
        ) : null}
        <Group position="apart" h={26} py={2.5} align="center">
          <Group
            component="dl"
            position="apart"
            spacing={12}
            css={{
              [desktopFirstMediaQuery.mobile]: {
                'svg, dd': { fontSize: '12px !important' },
              },
            }}
          >
            <Group spacing={4}>
              <dt>
                <Icon
                  size="sm"
                  color="gray.6"
                  icon={isQuestion(type) || isAnswerType ? faThumbsUp : faHeart}
                />

                <VisuallyHidden>{t('좋아요수')}</VisuallyHidden>
              </dt>
              <Text component="dd" size="sm" weight={500} color="gray.6">
                {formatCompactNumber(recommendedCount)}
              </Text>
            </Group>
            <Group spacing={4}>
              <dt>
                <Icon size="sm" color="gray.6" icon={faComment} />
                <VisuallyHidden>{t('댓글수')}</VisuallyHidden>
              </dt>
              <Text component="dd" size="sm" weight={500} color="gray.6">
                {formatCompactNumber(commentCount)}
              </Text>
            </Group>
            <Group spacing={4}>
              <dt>
                <Icon size="sm" color="gray.6" icon={faEye} />
                <VisuallyHidden>{t('조회수')}</VisuallyHidden>
              </dt>
              <Text component="dd" size="sm" weight={500} color="gray.6">
                {formatCompactNumber(viewCount)}
              </Text>
            </Group>
          </Group>
          {status && isQuestion(type) && isResolvedStatus(status) ? (
            <Badge
              radius="xl"
              color="blue"
              size="lg"
              variant="light"
              leftSection={<Icon icon={faCheck} size="xs" />}
              css={{ [desktopFirstMediaQuery.mobile]: { fontSize: 13 } }}
            >
              {t(POST_STATUS[status])}
            </Badge>
          ) : null}
        </Group>
      </Box>
    </a>
  );
}

function PostStatusText({ type, status }: { type: string; status: NonNullable<PostStatus> }) {
  const { t } = useTranslation();
  const isGrayColorText = ['unresolved', 'recruited'].includes(status);

  if (isGrayColorText) {
    return (
      <Text size="sm" weight={700} color="gray.5">
        {t(POST_STATUS[status])}
      </Text>
    );
  }

  if (isQuestion(type)) {
    return (
      <Text size="sm" weight={700} color="blue.6">
        {t(POST_STATUS[status])}
      </Text>
    );
  }

  if (isStudy(type)) {
    return (
      <Text size="sm" weight={700} color="infgreen.6">
        {t(POST_STATUS[status])}
      </Text>
    );
  }

  return null;
}
